import {
  Examination_Type,
  ExaminationListQueryVariables,
  PlatformExaminationListOrderFields,
} from "@generated/graphql";
import { Chip, Stack } from "@mui/material";
import DownloadReportButton from "components/buttons/DownloadReportButton";
import ColoredPercentage from "components/ColoredPercentage";
import TestingLink from "components/TestingLink";
import TestingStatus from "components/TestingStatus";
import { ASSESSMENT_TYPE_TITLE, EXAMINATION_TYPE_TITLE, SCORING_TYPE_TITLE } from "constants/global";
import typography from "constants/typography.constants";
import dayjs from "dayjs";
import { getTrialLinkString, gradeColor, secondsToMinutes } from "helpers/index";
import { ColumnConfig, RouteEnum } from "layout/TableConstructor/types";
import { ExaminationItem } from "../types";

const examinationListBaseColumnConfig: ColumnConfig<
  ExaminationItem,
  NonNullable<ExaminationListQueryVariables["filter"]>,
  PlatformExaminationListOrderFields,
  "trials",
  RouteEnum.Examination
>[] = [
  {
    title: "ID тестирования",
    name: "numericId",
    filterName: "numericId",
    sortField: PlatformExaminationListOrderFields.NumericId,
    render: (data) => data.numericId,
    renderCollapse: (data) => data.numericId,
  },
  {
    title: "Название тестирования",
    name: "title",
    filterName: "title",
    sortField: PlatformExaminationListOrderFields.Title,
    render: (data) => data.title,
  },
  {
    title: "Название КК",
    name: "competencyMapTitle",
    filterName: "competencyMapTitle",
    sortField: PlatformExaminationListOrderFields.CompetencyMapTitle,
    render: (data) => data.competencyMap?.title,
  },
  // TODO: вернуть при интеграции
  // {
  //   title: "Название сценария",
  //   name: "scenarioTitle",
  //   filterName: "scenarioTitle",
  //   sortField: PlatformExaminationListOrderFields.ScenarioTitle,
  //   render: (data) => data.scenario?.title || "-",
  // },
  {
    title: "Специализация",
    name: "specialization",
    filterName: "specialization",
    sortField: PlatformExaminationListOrderFields.CompetencyMapSpecialization,
    render: (data) => data.competencyMap?.specialization,
  },
  {
    title: "Количество компетенций",
    name: "competencesCount",
    filterName: "competencesCount",
    render: (data) => data.competencyMap?.competencesCount || 0,
  },
  {
    title: "Количество блоков знаний",
    name: "blocksCount",
    filterName: "blocksCount",
    render: (data) => data.competencyMap?.blocksCount || 0,
  },
  {
    title: "ID кандидата",
    name: "candidateNumericId",
    filterName: "candidateNumericId",
    render: (data) => {
      const isPersonal = data.type === Examination_Type.Personal;
      if (isPersonal) return data.trials?.[0]?.companyCandidate.numericId;
      return "-";
    },
    renderCollapse: (data) => data.companyCandidate.numericId,
  },
  {
    title: "Кандидат",
    name: "candidate",
    filterName: "candidateFullName",
    render: (data) => {
      const isPersonal = data.type === Examination_Type.Personal;
      if (isPersonal) return data.trials?.[0]?.companyCandidate.fullName;
      return new Set(data.trials?.map((t) => t.companyCandidate.id)).size || 0;
    },
    renderCollapse: (data) => data.companyCandidate.fullName,
  },
  {
    title: "Дата создания",
    name: "createdAt",
    filterName: "createdAt",
    sortField: PlatformExaminationListOrderFields.CreatedAt,
    render: (data) => {
      const isPersonal = data.type === Examination_Type.Personal;
      const createdAt = isPersonal ? data.trials?.[0]?.createdAt : data.createdAt;
      if (!createdAt) return "-";
      return dayjs(createdAt).format("DD.MM.YYYY");
    },
    renderCollapse: (data) => {
      const { createdAt } = data;
      if (!createdAt) return "-";
      return dayjs(createdAt).format("DD.MM.YYYY");
    },
  },
  {
    title: "Дата завершения",
    name: "completedAt",
    filterName: "completedAt",
    render: (data) => {
      const isPersonal = data.type === Examination_Type.Personal;
      const completedAt = isPersonal ? data.trials?.[0]?.completedAt : data.lastCompleteDate;
      if (!completedAt) return "-";
      return dayjs(completedAt).format("DD.MM.YYYY");
    },
    renderCollapse: (data) => {
      const { completedAt } = data;
      if (!completedAt) return "-";
      return dayjs(completedAt).format("DD.MM.YYYY");
    },
  },
  {
    title: "Статус",
    name: "status",
    filterName: "trialStatus",
    render: (data) => {
      const isPersonal = data.type === Examination_Type.Personal;
      const status = isPersonal ? data.trials?.[0]?.status : undefined;
      if (!status) return "-";
      return (
        <TestingStatus
          sx={{ backgroundColor: "transparent", p: 0, "& .MuiTypography-root": { fontSize: 14 } }}
          status={status}
        />
      );
    },
    renderCollapse: (data) => {
      const { status } = data;
      return (
        <TestingStatus
          sx={{ backgroundColor: "transparent", p: 0, "& .MuiTypography-root": { fontSize: 14 } }}
          status={status}
        />
      );
    },
  },
  {
    title: "Грейды",
    name: "grades",
    filterName: "grades",
    render: (data) => (
      <Stack
        direction='row'
        spacing={0.5}>
        {data.competencyMap?.grades?.map((grade) => (
          <Chip
            key={grade}
            label={grade}
            sx={{ backgroundColor: `${gradeColor(grade)}.light`, color: `${gradeColor(grade)}.contrast` }}
          />
        ))}
      </Stack>
    ),
  },
  {
    title: "Результаты",
    name: "result",
    filterName: "candidateScore",
    render: (data) => {
      const isPersonal = data.type === Examination_Type.Personal;
      const trial = data.trials?.[0];
      return isPersonal ? trial?.candidateScore : "";
    },
    renderCollapse: (data) => data.candidateScore,
  },
  {
    title: "Время прохождения ожидаемое",
    name: "questionsTime",
    filterName: "expectedTimeOfTesting",
    render: (data) => {
      const isPersonal = data.type === Examination_Type.Personal;
      return isPersonal ? secondsToMinutes(data.questionsTime) : "-";
    },
    renderCollapse: (data) => secondsToMinutes(data.examination.questionsTime),
  },
  {
    title: "Время прохождения реальное",
    name: "candidateTime",
    filterName: "timeOfTesting",
    render: (data) => {
      const isPersonal = data.type === Examination_Type.Personal;
      const trial = data.trials?.[0];
      return isPersonal ? secondsToMinutes(trial?.candidateTime) : "-";
    },
    renderCollapse: (data) => secondsToMinutes(data.candidateTime),
  },
  {
    title: typography.testing.table.type,
    name: "type",
    filterName: "examinationType",
    sortField: PlatformExaminationListOrderFields.Type,
    render: (data) => (data.type ? EXAMINATION_TYPE_TITLE[data.type] : "-"),
  },
  {
    title: typography.testing.table.assessmentType,
    name: "assessmentType",
    filterName: "assessmentType",
    render: (data) => (data.assessmentType ? ASSESSMENT_TYPE_TITLE[data.assessmentType] : "-"),
  },
  {
    title: typography.testing.table.scoringType,
    name: "scoringType",
    render: (data) => (data.scoringType ? SCORING_TYPE_TITLE[data.scoringType] : "-"),
  },
  {
    title: typography.testing.table.aiGenerationPercentage,
    name: "aiGenerationPercentage",
    filterName: "aiGenerationPercentage",
    render: (data) => {
      const isPersonal = data.type === Examination_Type.Personal;
      if (isPersonal) return <ColoredPercentage percentage={data.trials?.[0]?.reportData?.ai_generation_percentage} />;
      return "-";
    },
    renderCollapse: (data) => <ColoredPercentage percentage={data.reportData?.ai_generation_percentage} />,
  },
  {
    title: "Оценка репозитория",
    name: "isRepositoryCheck",
    filterName: "isRepositoryCheck",
    sortField: PlatformExaminationListOrderFields.CompetencyMapIsRepositoryCheck,
    render: (data) => (data.competencyMap?.isRepositoryCheck ? "Да" : "Нет"),
  },
  {
    title: "Ссылка на тестирование",
    name: "link",
    render: (examination) => {
      const isGroup = examination.type === Examination_Type.Group;

      if (!isGroup) {
        const isPeronal = examination.type === Examination_Type.Personal;
        const link = getTrialLinkString(examination, isPeronal ? examination.trials?.[0] : undefined);

        if (!link) return "-";

        return (
          <TestingLink
            withoutLabel
            link={link}
          />
        );
      }
      return "-";
    },
    renderCollapse: (trial) => {
      const link = getTrialLinkString(trial.examination, trial);

      if (!link) return "-";

      return (
        <TestingLink
          withoutLabel
          link={link}
        />
      );
    },
  },
  {
    title: "Скачать отчет",
    name: "action",
    render: (data) => {
      return <DownloadReportButton examination={data} />;
    },
    renderCollapse: (data) => {
      return <DownloadReportButton trial={data} />;
    },
  },
];

export default examinationListBaseColumnConfig;
